import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Book, LogOut, User, Link2, Settings as SettingsIcon, Scan } from 'lucide-react';

const Header = ({ isAuthenticated, setIsAuthenticated, onShowAuth }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    navigate('/');
  };

  // Don't show header on flip-book view for immersive experience
  if (location.pathname.includes('/flipbook')) {
    return null;
  }

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Brand */}
          <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center gap-2 group">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-rose-500 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
              <Book className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-xl font-bold bg-gradient-to-r from-amber-600 to-rose-600 bg-clip-text text-transparent">
                PictureBook.co
              </span>
              <p className="text-xs text-gray-500 leading-none">AI Photo Albums</p>
            </div>
          </Link>

          {/* Navigation and Actions */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                {/* Quick Actions for authenticated users */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/connected-accounts')}
                  className="hidden md:flex items-center gap-2"
                >
                  <Link2 className="w-4 h-4" />
                  <span>Accounts</span>
                </Button>

                {/* User Menu */}
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2"
                    data-testid="user-menu-btn"
                  >
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-rose-400 flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="hidden sm:inline">{user.name?.split(' ')[0] || 'Account'}</span>
                  </Button>

                  {/* Dropdown Menu */}
                  {showUserMenu && (
                    <>
                      {/* Backdrop */}
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setShowUserMenu(false)}
                      />
                      
                      {/* Menu */}
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                          <p className="text-xs text-amber-600 font-medium mt-1">
                            {user.subscription_tier === 'paid' ? 'Premium' : 'Free'} Plan
                          </p>
                        </div>
                        
                        <button
                          onClick={() => {
                            navigate('/dashboard');
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Book className="w-4 h-4" />
                          My Albums
                        </button>

                        <button
                          onClick={() => {
                            navigate('/library');
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Book className="w-4 h-4" />
                          Photo Library
                        </button>

                        <button
                          onClick={() => {
                            navigate('/library-manager');
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-l-4 border-blue-500"
                        >
                          <Scan className="w-4 h-4" />
                          AI Library Manager
                        </button>

                        <button
                          onClick={() => {
                            navigate('/connected-accounts');
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Link2 className="w-4 h-4" />
                          Connected Accounts
                        </button>

                        {user.is_admin && (
                          <button
                            onClick={() => {
                              navigate('/admin');
                              setShowUserMenu(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <SettingsIcon className="w-4 h-4" />
                            Admin Panel
                          </button>
                        )}

                        <div className="border-t border-gray-100 my-1" />

                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          data-testid="logout-btn"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Guest Actions */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onShowAuth}
                  data-testid="header-signin-btn"
                  className="hidden sm:inline-flex"
                >
                  Sign In
                </Button>
                <Button
                  size="sm"
                  onClick={onShowAuth}
                  data-testid="header-getstarted-btn"
                  className="bg-gradient-to-r from-amber-600 to-rose-500 text-white"
                >
                  Get Started Free
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
